"use client"

import { useServiceFormApply } from "@/hook/useServiceFormApply"
import { t } from "@/languages/i18n"
import { ServiceFormApplyProps } from "@/types/types"
import { Alert, Form } from "antd"
import Link from "next/link"

const ServiceFormApply: React.FC<ServiceFormApplyProps> = ({ jobDetail, locale, phoneNumber }) => {
  const [form] = Form.useForm()

  const {
    showAlert,
    isMessageSent,
    loading,
    selectedFile,
    fileInputRef,
    handleUploadClick,
    handleFileChange,
    onFinish,
  } = useServiceFormApply({
    jobDetail,
    locale,
    form,
  })

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onFinish} className="space-y-4">
        <h4 className="mb-4 text-xl leading-[1.8125rem] sm:text-3xl sm:leading-[2rem] font-black text-[#333333]">
          {t("Apply for")} {t("position")}: {jobDetail?.attributes?.position}
          {phoneNumber && ` (${phoneNumber})`}
        </h4>

        <p className="mb-4 text-[#333333] font-normal italic text-sm">{t("require")} *</p>

        <Form.Item
          name="fullname"
          className="text-base"
          label={<span className="text-[#333] text-base font-medium">{t("Full Name")}</span>}
          rules={[
            {
              required: true,
              message: t("Please fill in the input"),
            },
          ]}
        >
          <input
            type="text"
            placeholder="John Doe"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#18af4e] focus:border-transparent placeholder:text-[#6c757d] bg-white"
          />
        </Form.Item>

        <Form.Item
          label={<span className="text-[#333] text-base font-medium">{t("Phone number")}</span>}
          name="phone"
          rules={[
            {
              required: true,
              message: t("Please fill in the input"),
            },
            {
              pattern: /^[0-9+\-\s()]*$/,
              message: t("Please enter a valid phone number"),
            },
            { pattern: /^[a-zA-Z0-9]+$/, message: t("Số điện thoại phải là 0-9") },
          ]}
        >
          <input
            type="tel"
            inputMode="numeric"
            placeholder={t("Phone number")}
            onKeyPress={e => {
              if (!/[0-9+\-\s()]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                e.preventDefault()
              }
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#18af4e] focus:border-transparent placeholder:text-[#6c757d] bg-white"
          />
        </Form.Item>

        <Form.Item
          label={<span className="text-[#333] text-base font-medium">{t("Email address")}</span>}
          name="email"
          rules={[
            {
              required: true,
              message: t("Please fill in the input"),
            },
            {
              pattern:
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              message: t("Please enter a valid email"),
            },
          ]}
        >
          <input
            type="text"
            placeholder={t("Email address")}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#18af4e] focus:border-transparent placeholder:text-[#6c757d] bg-white"
          />
        </Form.Item>

        <Form.Item
          label={<span className="text-[#333] text-base font-medium">{t("Self introduction")}</span>}
          name="selfInstruction"
        >
          <textarea
            rows={4}
            placeholder={t("Tell us about yourself")}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#18af4e] focus:border-transparent placeholder:text-[#6c757d] resize-none bg-white"
          />
        </Form.Item>

        <Form.Item
          name="cvFileUpload"
          rules={[
            {
              required: true,
              message: t("Please upload your CV here"),
            },
          ]}
        >
          <div className="flex items-center gap-3">
            <label className="text-[#333] text-base font-medium whitespace-nowrap">
              {t("Upload Application Form")} *
            </label>
            <input
              type="file"
              accept=".doc,.docx,.pdf,.png,.jpg,.jpeg,.xlsx"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <button
              type="button"
              onClick={handleUploadClick}
              className="px-[0.375rem] py-[0.125rem] bg-[#EBFBEE] text-[#37B24D] rounded-md hover:bg-[#157e40] transition-colors font-medium"
            >
              {t("Upload")}
            </button>
            {selectedFile && <span className="text-sm text-[#18af4e] font-medium">{selectedFile.name}</span>}
          </div>
        </Form.Item>

        <Form.Item
          name="policy"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error(t("You need to accept this before submit the form"))),
            },
          ]}
        >
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-[#37D2B4] focus:ring-[#37D2B4] cursor-pointer"
            />
            <p className="text-sm text-[#333] leading-5">
              {t("I agree with")}{" "}
              <Link href="/about-us" className="text-[#18af4e] hover:underline">
                {t("Terms of service")}
              </Link>{" "}
              {t("and")}{" "}
              <Link href="/about-us" className="text-[#18af4e] hover:underline">
                {t("Privacy policy")}
              </Link>
              .
            </p>
          </div>
        </Form.Item>

        <div className="mt-6">
          <button
            type="submit"
            disabled={loading}
            className="w-[4.625rem] h-[2.25rem] bg-[#18af4e] text-white rounded-sm hover:bg-[#157e40] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("Send")}
          </button>
        </div>
      </Form>

      {showAlert && (
        <div className="mt-4">
          <Alert
            description={
              isMessageSent
                ? t("Submitted, thanks for your apply and We will keep you in touch")
                : t("Something went wrong")
            }
            type={isMessageSent ? "success" : "error"}
          />
        </div>
      )}
    </>
  )
}

export default ServiceFormApply
